import { computed, ref, shallowRef } from 'vue'

import { defineStore } from 'pinia'
import { rand } from '@vueuse/core'

import api from '@/services/api'
import { useDashboardStore } from '@/stores/dashboard'
import { useEditorDefaultStore } from '@/stores/editor/default'
import { useEditorPageStore } from '@/stores/editor/page'
import { useLayoutStore } from '@/stores/layout'
import { useSiteStore } from '@/stores/site'
import {
    type BetaEditorPreviewDevice,
    type BetaEditorPreviewSurface,
} from '@/utils/editor-beta-preview'
import {
    getPreviewSubpagesContent,
    isImportedHomePreviewCandidate,
} from '@/utils/editor-home-preview'
import {
    normalizeEditorFrameType,
    sanitizeEditorFrameForPayload,
} from '@/utils/editor-payload'

type ActiveTextSelectionState = {
    bold: boolean
    italic: boolean
    underline: boolean
    strike: boolean
    bulletList: boolean
    orderedList: boolean
    textAlign: 'left' | 'center' | 'right' | 'justify' | null
    fontSize: string | null
}

type ActiveTextSelectionRange = {
    from: number
    to: number
}

const createDefaultActiveTextSelectionState = (): ActiveTextSelectionState => ({
    bold: false,
    italic: false,
    underline: false,
    strike: false,
    bulletList: false,
    orderedList: false,
    textAlign: null,
    fontSize: null,
})

const cloneValue = <T>(value: T): T => JSON.parse(JSON.stringify(value))

const isPlainObject = (value: any) => (
    value !== null
    && typeof value === 'object'
    && !Array.isArray(value)
)

const mergePlainObjects = (currentValue: any, nextValue: any): any => {
    if (!isPlainObject(currentValue) || !isPlainObject(nextValue)) {
        return nextValue
    }

    return Object.entries(nextValue).reduce((result, [key, value]) => {
        result[key] = mergePlainObjects(result[key], value)

        return result
    }, { ...currentValue } as Record<string, any>)
}

export const useEditorBetaStore = defineStore('editor-beta', () => {
    const dashboardStore = useDashboardStore()
    const editorDefaultStore = useEditorDefaultStore()
    const editorPageStore = useEditorPageStore()
    const layoutStore = useLayoutStore()
    const siteStore = useSiteStore()

    const previewDevice = ref<BetaEditorPreviewDevice>('desktop')
    const previewSurface = ref<BetaEditorPreviewSurface>('editor')
    const selectedSectionId = ref<any>(null)
    const selectedSectionIndex = ref<number | null>(null)
    const selectedElementId = ref<any>(null)
    const selectedElementType = ref<string | null>(null)
    const editingElementId = ref<any>(null)
    const sidebarReference = ref<string | null>(null)
    const elementForEdit = ref<any>(null)
    const activeTextEditor = shallowRef<any>(null)
    const activeTextSelectionState = ref<ActiveTextSelectionState>(createDefaultActiveTextSelectionState())
    const activeTextSelectionRange = ref<ActiveTextSelectionRange | null>(null)
    const importedSectionPreviews = ref<Record<string, { pageMeta: any | null; previewSection: any | null }>>({})
    const pendingSectionCreations = ref(0)

    const activePage = computed(() => editorPageStore.activePage)
    const isMobilePreview = computed(() => previewDevice.value === 'mobile')
    const isSitePreviewActive = computed(() => previewSurface.value === 'site')
    const sectionForEdit = computed(() => {
        if (selectedSectionIndex.value === null) return null

        const section = activePage.value?.content?.[selectedSectionIndex.value]

        return section
            ? {
                ...section,
                sectionIndex: selectedSectionIndex.value,
            }
            : null
    })

    const getElementId = (element: any) => element?.frameId || element?.temporaryId || null
    const getSectionId = (section: any) => section?.frameId || section?.temporaryId || null

    const normalizePresetExtra = (extra: any) => {
        if (!extra) return {}

        if (typeof extra === 'string') {
            try {
                return JSON.parse(extra)
            } catch {
                return {}
            }
        }

        return cloneValue(extra)
    }

    const createElementSkeleton = (frameType: string) => {
        const normalizedFrameType = normalizeEditorFrameType(frameType)

        switch (normalizedFrameType) {
            case 'title':
                return cloneValue(editorDefaultStore.newElementTitle)
            case 'text':
                return cloneValue(editorDefaultStore.newElementText)
            case 'image':
                return cloneValue(editorDefaultStore.newElementImage)
            case 'video':
                return cloneValue(editorDefaultStore.newElementVideo)
            case 'gallery':
                return cloneValue(editorDefaultStore.newElementGallery)
            case 'comment':
                return cloneValue(editorDefaultStore.newElementComment)
            default:
                return {
                    action: null,
                    actionType: 0,
                    component: null,
                    extra: {},
                    frameContent: null,
                    frameDataId: null,
                    frameId: null,
                    frameOrigin: null,
                    frameStatus: 1,
                    frameType: normalizedFrameType,
                    grid: 12,
                    gridMobile: 12,
                }
        }
    }

    const transformPresetFrameToElement = (frame: any): any => {
        const frameType = normalizeEditorFrameType(String(frame?.type ?? 'text'))
        const element = {
            ...createElementSkeleton(frameType),
            temporaryId: rand(0, 9999999999999),
            frameId: null,
            frameDataId: null,
            frameType,
            component: frame?.component ?? null,
            frameContent: cloneValue(frame?.content ?? ''),
            grid: frame?.grid ?? 12,
            gridMobile: frame?.gridMobile ?? frame?.grid ?? 12,
            new: true,
        }

        const normalizedExtra = normalizePresetExtra(frame?.extra)

        if (Object.keys(normalizedExtra).length > 0) {
            element.extra = normalizedExtra
        }

        if (Array.isArray(frame?.frames)) {
            element.frameContent = frame.frames.map(transformPresetFrameToElement)
        }

        return element
    }

    const buildTextElement = (options?: { newBefore?: boolean }) => ({
        temporaryId: rand(0, 9999999999999),
        ...cloneValue(editorDefaultStore.newElementText),
        frameId: null,
        frameDataId: null,
        extra: {
            margin: {
                ...editorDefaultStore.margin,
            },
            padding: {
                ...editorDefaultStore.padding,
            },
        },
        new: true,
        ...(options?.newBefore ? { newBefore: true } : {}),
    })

    const getCreatedSectionIds = (data: any) => {
        const ids = data?.id ?? data ?? {}

        return {
            frameId: ids.frame ?? ids.frameId ?? null,
            frameDataId: ids.frameDataId
                ?? ids.pageFrameId
                ?? ids.pageFrame
                ?? ids.page_frame_id
                ?? ids.page_frame
                ?? null,
        }
    }

    const resolveCreatedSectionFrameDataId = async (frameId: any) => {
        if (frameId == null || activePage.value?.id == null) return null

        try {
            const response = await api.get(`site/${siteStore.id}/post/${activePage.value.id}/edit`)
            const { status, data } = await response.json()

            if (status != '1') return null

            const createdSection = data?.content?.find((section: any) => section?.frameId === frameId)

            return createdSection?.frameDataId ?? null
        } catch (error) {
            console.warn(error)
            return null
        }
    }

    const buildSectionFromPreset = (preset?: any, createdIds?: { frameId?: any; frameDataId?: any }) => {
        const sectionExtra = normalizePresetExtra(preset?.component_variables)
        const isPersistedSection = createdIds?.frameId != null || createdIds?.frameDataId != null
        const section = {
            ...cloneValue(editorDefaultStore.newSection),
            betaClientKey: rand(0, 9999999999999),
            ...(isPersistedSection ? {} : {
                temporaryId: rand(0, 9999999999999),
                new: true,
            }),
            frameId: createdIds?.frameId ?? null,
            frameDataId: createdIds?.frameDataId ?? null,
            extra: {
                ...cloneValue(editorDefaultStore.newSection.extra),
                ...sectionExtra,
            },
            frameContent: [] as any[],
        }

        if (preset && Array.isArray(preset.frames) && preset.frames.length > 0) {
            section.frameContent = preset.frames.map(transformPresetFrameToElement)
            return section
        }

        section.frameContent = [
            {
                ...cloneValue(editorDefaultStore.newElementTitle),
                temporaryId: rand(0, 9999999999999),
                frameId: null,
                frameDataId: null,
                frameContent: '',
                extra: {
                    ...cloneValue(editorDefaultStore.newElementTitle.extra),
                    style: {
                        ...cloneValue(editorDefaultStore.newElementTitle.extra.style),
                        fontSize: '32px',
                        fontWeight: '700',
                        textAlign: 'left',
                        color: '',
                    },
                    style_font_size_lock: false,
                    style_font_weight_lock: false,
                    style_text_align_lock: false,
                },
                new: true,
            },
            buildTextElement(),
        ]

        return section
    }

    const setPreviewDevice = (value: BetaEditorPreviewDevice) => {
        previewDevice.value = value
    }

    const setPreviewSurface = (value: BetaEditorPreviewSurface) => {
        previewSurface.value = value
    }

    const togglePreviewSurface = () => {
        if (previewSurface.value === 'site') {
            previewSurface.value = 'editor'
            previewDevice.value = 'desktop'
            return
        }

        previewSurface.value = 'site'
    }

    const findSectionIndexById = (sectionId: any) => {
        if (sectionId == null) return -1

        return (activePage.value?.content ?? []).findIndex((section: any) => (
            getSectionId(section) === sectionId
        ))
    }

    const syncSelectedSectionContext = () => {
        if (selectedSectionId.value == null) return

        const nextSectionIndex = findSectionIndexById(selectedSectionId.value)

        if (nextSectionIndex < 0) {
            clearSelectedSection()
            clearSelectedElement()
            clearEditingElement()
            return
        }

        selectedSectionIndex.value = nextSectionIndex
        editorPageStore.setSectionForEdit(sectionForEdit.value)

        if (elementForEdit.value?.schema?.[0]) {
            const schema = elementForEdit.value.schema.map((item: any, index: number) => (
                index === 0 ? { ...item, index: nextSectionIndex } : item
            ))

            elementForEdit.value = {
                ...elementForEdit.value,
                schema,
                sectionIndex: nextSectionIndex,
            }
            editorPageStore.setElementForEdit(elementForEdit.value)
        }
    }

    const setSelectedSectionByIndex = (sectionIndex: number) => {
        const section = activePage.value?.content?.[sectionIndex]

        if (!section) return

        selectedSectionId.value = getSectionId(section)
        selectedSectionIndex.value = sectionIndex
        editorPageStore.setSectionForEdit(sectionForEdit.value)
    }

    const setSelectedSection = (section: any, sectionIndex?: number) => {
        const resolvedIndex = typeof sectionIndex === 'number'
            ? sectionIndex
            : findSectionIndexById(getSectionId(section))

        if (resolvedIndex < 0) return

        setSelectedSectionByIndex(resolvedIndex)
    }

    const clearSelectedSection = () => {
        selectedSectionId.value = null
        selectedSectionIndex.value = null
        editorPageStore.setSectionForEdit(null)
    }

    const isSelectedSection = (section: any, sectionIndex?: number) => {
        if (selectedSectionId.value != null) {
            return getSectionId(section) === selectedSectionId.value
        }

        return typeof sectionIndex === 'number' && selectedSectionIndex.value === sectionIndex
    }

    const setSelectedElement = (element: any) => {
        const elementId = getElementId(element)

        if (!elementId) return

        selectedElementId.value = elementId
        selectedElementType.value = element?.frameType ?? null
    }

    const clearSelectedElement = () => {
        selectedElementId.value = null
        selectedElementType.value = null
    }

    const setEditingElement = (element: any) => {
        const elementId = getElementId(element)

        if (!elementId) return

        setSelectedElement(element)
        editingElementId.value = elementId
    }

    const clearEditingElement = () => {
        editingElementId.value = null
    }

    const isEditingElement = (elementId: any) => editingElementId.value === elementId
    const isElementActive = (elementId: any) => (
        selectedElementId.value === elementId || editingElementId.value === elementId
    )

    const setActiveTextEditor = (editor: any) => {
        activeTextEditor.value = editor
    }

    const clearActiveTextEditor = () => {
        activeTextEditor.value = null
        activeTextSelectionState.value = createDefaultActiveTextSelectionState()
        activeTextSelectionRange.value = null
    }

    const setActiveTextSelectionState = (value: ActiveTextSelectionState) => {
        activeTextSelectionState.value = value
    }

    const setActiveTextSelectionRange = (selection: any) => {
        const from = Number(selection?.from)
        const to = Number(selection?.to)

        if (!Number.isFinite(from) || !Number.isFinite(to)) {
            activeTextSelectionRange.value = null
            return
        }

        activeTextSelectionRange.value = { from, to }
    }

    const getTextEditorCommandChain = (editor = activeTextEditor.value) => {
        if (!editor) return null

        const selection = activeTextSelectionRange.value

        if (selection) {
            try {
                editor.commands.setTextSelection(selection)
            } catch (error) {
                console.warn(error)
            }
        }

        return editor.chain().focus()
    }

    const shouldKeepActiveTextSelection = () => {
        const editorSelection = activeTextSelectionRange.value ?? activeTextEditor.value?.state?.selection

        if (editorSelection && editorSelection.from !== editorSelection.to) {
            return true
        }

        if (typeof window === 'undefined') {
            return false
        }

        const browserSelection = window.getSelection?.()
        const selectedText = browserSelection?.toString?.().trim() ?? ''

        return selectedText.length > 0
    }

    const setElementForEdit = (element: any | null) => {
        elementForEdit.value = element
        editorPageStore.setElementForEdit(element)
    }

    const setSidebarReference = (value: string | null) => {
        sidebarReference.value = value
    }

    const clearSidebarTarget = () => {
        setSidebarReference(null)
        setElementForEdit(null)
        clearSelectedElement()
        clearEditingElement()
        clearActiveTextEditor()
    }

    const mountSchemaLevels = (schema: any) => {
        const levels: any[] = []

        if (!Array.isArray(schema)) return levels

        schema.forEach((item: any) => {
            if (item.level === 0) {
                levels[item.level] = {
                    level: item.level,
                    index: item.index,
                    type: item.element,
                    element: activePage.value?.content?.[item.index] ?? null,
                }
                return
            }

            const parent = levels[item.level - 1]?.element

            levels[item.level] = {
                level: item.level,
                index: item.index,
                type: item.element,
                element: parent?.frameContent?.[item.index] ?? null,
            }
        })

        return levels
    }

    const updateElementCascade = (lastIndex: number, levels: any[]) => {
        for (let index = lastIndex; index >= 0; index--) {
            const currentLevel = levels[index]
            const parentLevel = levels[index - 1]

            if (parentLevel) {
                parentLevel.element.frameContent.splice(currentLevel.index, 1, currentLevel.element)
            } else {
                activePage.value.content.splice(currentLevel.index, 1, currentLevel.element)
            }
        }
    }

    const getElementBySchema = (schema: any) => {
        const levels = mountSchemaLevels(schema)

        if (!levels.length) return null

        return levels[levels.length - 1]?.element ?? null
    }

    const updateElementBySchema = (schema: any, nextElement: any) => {
        const levels = mountSchemaLevels(schema)

        if (!levels.length) return

        const persistableElement = sanitizeEditorFrameForPayload(nextElement)
        const lastIndex = levels.length - 1
        levels[lastIndex].element = persistableElement
        updateElementCascade(lastIndex, levels)

        if (elementForEdit.value && getElementId(elementForEdit.value) === getElementId(persistableElement)) {
            setElementForEdit({
                ...persistableElement,
                schema,
                sectionIndex: schema?.[0]?.index,
            })
        }
    }

    const patchElementBySchema = (schema: any, patch: Record<string, any>) => {
        const levels = mountSchemaLevels(schema)

        if (!levels.length) return

        const lastIndex = levels.length - 1
        const currentElement = levels[lastIndex]?.element

        if (!currentElement) return

        const nextElement = {
            ...currentElement,
            ...patch,
        }

        if ('extra' in patch) {
            nextElement.extra = mergePlainObjects(currentElement.extra ?? {}, patch.extra ?? {})
        }

        if ('frameContent' in patch) {
            nextElement.frameContent = mergePlainObjects(currentElement.frameContent, patch.frameContent)
        }

        updateElementBySchema(schema, nextElement)
    }

    const updateElementForEdit = () => {
        if (!elementForEdit.value?.schema) return

        updateElementBySchema(elementForEdit.value.schema, elementForEdit.value)
    }

    const setSelectedElementBySchema = (schema: any, fallbackElement?: any) => {
        const element = fallbackElement ?? getElementBySchema(schema)

        if (!element) return

        setSelectedSectionByIndex(schema?.[0]?.index)
        setSelectedElement(element)
        setElementForEdit({
            ...element,
            schema,
            sectionIndex: schema?.[0]?.index,
        })
        const schemaFrameType = Array.isArray(schema) ? schema[schema.length - 1]?.element : null

        setSidebarReference(element.frameType ?? schemaFrameType ?? 'default')
    }

    const setEditingElementBySchema = (schema: any, fallbackElement?: any) => {
        const element = fallbackElement ?? getElementBySchema(schema)

        if (!element) return

        setSelectedElementBySchema(schema, element)
        setEditingElement(element)
    }

    const getElementMoveContextBySchema = (schema: any) => {
        const levels = mountSchemaLevels(schema)

        if (levels.length < 2) return null

        const lastIndex = levels.length - 1
        const lastLevel = levels[lastIndex]
        const parentLevel = levels[lastIndex - 1]
        const siblings = parentLevel?.element?.frameContent

        if (!Array.isArray(siblings)) return null

        return {
            levels,
            lastIndex,
            lastLevel,
            parentLevel,
            siblings,
        }
    }

    const isFirstElementBySchema = (schema: any) => {
        const context = getElementMoveContextBySchema(schema)

        return !context || context.lastLevel.index === 0
    }

    const isLastElementBySchema = (schema: any) => {
        const context = getElementMoveContextBySchema(schema)

        return !context || context.lastLevel.index === context.siblings.length - 1
    }

    const moveElementBySchema = (schema: any, direction: 'up' | 'down') => {
        const context = getElementMoveContextBySchema(schema)

        if (!context) return

        const offset = direction === 'up' ? -1 : 1
        const targetIndex = context.lastLevel.index + offset

        if (targetIndex < 0 || targetIndex >= context.siblings.length) return

        const [movedElement] = context.siblings.splice(context.lastLevel.index, 1)
        context.siblings.splice(targetIndex, 0, movedElement)
        updateElementCascade(context.lastIndex - 1, context.levels)

        const movedElementId = getElementId(movedElement)

        if (elementForEdit.value && getElementId(elementForEdit.value) === movedElementId) {
            const updatedSchema = schema.map((item: any, index: number) => (
                index === schema.length - 1 ? { ...item, index: targetIndex } : item
            ))

            setElementForEdit({
                ...elementForEdit.value,
                schema: updatedSchema,
            })
        }
    }

    const mountCloneElement = (elementForClone: any) => {
        const clonedElement = sanitizeEditorFrameForPayload(elementForClone)
        clonedElement.frameId = null
        clonedElement.frameDataId = null
        clonedElement.temporaryId = rand(0, 9999999999999)
        clonedElement.new = true

        if (Array.isArray(clonedElement.frameContent)) {
            clonedElement.frameContent = clonedElement.frameContent.map(mountCloneElement)
        }

        return clonedElement
    }

    const cloneElementBySchema = (schema: any) => {
        const context = getElementMoveContextBySchema(schema)

        if (!context) return

        const clonedElement = mountCloneElement(context.lastLevel.element)
        context.siblings.splice(context.lastLevel.index + 1, 0, clonedElement)
        updateElementCascade(context.lastIndex - 1, context.levels)
    }

    const removeElementBySchema = (schema: any) => {
        const context = getElementMoveContextBySchema(schema)

        if (!context) return

        const elementToRemove = context.lastLevel.element

        if (!elementToRemove) return

        if (editorPageStore.isProtectedHomeFirstGallery(elementToRemove)) return

        if (!elementToRemove.new && (elementToRemove.frameId || elementToRemove.frameDataId)) {
            editorPageStore.deletedFrames.push(sanitizeEditorFrameForPayload(elementToRemove))
        }

        context.siblings.splice(context.lastLevel.index, 1)
        updateElementCascade(context.lastIndex - 1, context.levels)

        if (getElementId(elementToRemove) === selectedElementId.value) {
            clearSidebarTarget()
        }
    }

    const addElementBySchema = (schema: any, structure: any, position = 0) => {
        const levels = mountSchemaLevels(schema)
        const persistableStructure = sanitizeEditorFrameForPayload(structure)

        if (levels.length === 1) {
            const insertIndex = levels[0].index + position
            activePage.value.content.splice(insertIndex, 0, persistableStructure)
            return insertIndex
        }

        if (levels.length > 0) {
            const parentLevel = levels[levels.length - 2]
            const insertIndex = levels[levels.length - 1].index + position
            parentLevel.element.frameContent.splice(insertIndex, 0, persistableStructure)
            updateElementCascade(levels.length - 2, levels)
            return insertIndex
        }

        return -1
    }

    const appendTextElementToSection = (sectionIndex: number, position?: number) => {
        const section = activePage.value?.content?.[sectionIndex]

        if (!section) return null

        const newElement = buildTextElement()
        const insertIndex = position ?? section.frameContent.length

        section.frameContent.splice(insertIndex, 0, newElement)
        activePage.value.content.splice(sectionIndex, 1, section)

        const schema = [
            { level: 0, element: 'section', index: sectionIndex },
            { level: 1, element: 'text', index: insertIndex },
        ]

        setSelectedSectionByIndex(sectionIndex)
        setSelectedElement(newElement)
        setEditingElement(newElement)
        setElementForEdit({
            ...newElement,
            schema,
            sectionIndex,
        })
        setSidebarReference('text')

        return {
            element: newElement,
            index: insertIndex,
            schema,
        }
    }

    const addSectionAtIndex = async (index: number, preset?: any) => {
        const normalizedIndex = Math.max(0, Math.min(index, activePage.value?.content?.length ?? 0))
        const nextSection = buildSectionFromPreset(preset)

        activePage.value.content.splice(normalizedIndex, 0, nextSection)
        setSelectedSectionByIndex(normalizedIndex)
        clearSelectedElement()
        clearEditingElement()
        setElementForEdit(null)
        setSidebarReference('section')

        pendingSectionCreations.value += 1
        editorPageStore.disabledPublish = true

        try {
            const response = await api.post(
                `site/${siteStore.id}/post/${activePage.value.id}/card`,
                JSON.stringify({ index: normalizedIndex }),
            )
            const data = await response.json()

            if (data.status != '1') {
                throw new Error('Error on add new section')
            }

            const createdIds = getCreatedSectionIds(data)

            if (createdIds.frameDataId == null) {
                createdIds.frameDataId = await resolveCreatedSectionFrameDataId(createdIds.frameId)
            }

            const sectionIndex = activePage.value.content.findIndex((section: any) => (
                section?.betaClientKey === nextSection.betaClientKey
            ))

            if (sectionIndex === -1) return nextSection

            const section = activePage.value.content[sectionIndex]

            section.frameId = createdIds.frameId
            section.frameDataId = createdIds.frameDataId
            delete section.new
            delete section.temporaryId

            activePage.value.content.splice(sectionIndex, 1, section)
            setSelectedSectionByIndex(sectionIndex)
        } catch (error) {
            const sectionIndex = activePage.value.content.findIndex((section: any) => (
                section?.betaClientKey === nextSection.betaClientKey
            ))

            if (sectionIndex > -1) {
                activePage.value.content.splice(sectionIndex, 1)
                syncSelectedSectionContext()
            }

            throw error
        } finally {
            pendingSectionCreations.value = Math.max(0, pendingSectionCreations.value - 1)

            if (pendingSectionCreations.value === 0) {
                editorPageStore.disabledPublish = false
            }
        }

        return nextSection
    }

    const deleteSectionAtIndex = async (sectionIndex: number) => {
        const section = activePage.value?.content?.[sectionIndex]

        if (!section) return
        if (activePage.value.content.length <= 1) return

        if (!section.new && section.frameDataId) {
            const response = await api.delete(
                `site/${siteStore.id}/post/${activePage.value.id}/card/${section.frameDataId}`,
            )
            const data = await response.json()

            if (data.status != '1') {
                throw new Error('Error on delete section')
            }
        } else if (!section.new && (section.frameId || section.frameDataId)) {
            editorPageStore.deletedSections.push(sanitizeEditorFrameForPayload(section))
        }

        activePage.value.content.splice(sectionIndex, 1)
        syncSelectedSectionContext()
    }

    const moveSection = async (sectionIndex: number, direction: 'up' | 'down') => {
        if (direction === 'up') {
            await editorPageStore.upSection(sectionIndex)
        } else {
            await editorPageStore.downSection(sectionIndex)
        }

        syncSelectedSectionContext()
    }

    const updateSectionExtra = (extra: any) => {
        if (selectedSectionIndex.value === null) return

        const currentSection = activePage.value?.content?.[selectedSectionIndex.value]

        if (!currentSection) return

        const nextSection = {
            ...currentSection,
            extra,
        }

        activePage.value.content.splice(selectedSectionIndex.value, 1, nextSection)
        editorPageStore.setSectionForEdit(sectionForEdit.value)
    }

    const setBackgroundImageSectionForEdit = (image: any) => {
        if (selectedSectionIndex.value === null) return

        const currentSection = activePage.value?.content?.[selectedSectionIndex.value]

        if (!currentSection) return

        const nextSection = {
            ...currentSection,
            backgroundImage: image,
        }

        activePage.value.content.splice(selectedSectionIndex.value, 1, nextSection)
        updateSectionExtra({
            ...nextSection.extra,
            background_image_opacity: '80',
        })
    }

    const getImportedSectionPreview = (sectionFrameId: number | string | null | undefined) => {
        if (sectionFrameId == null) return null

        return importedSectionPreviews.value[String(sectionFrameId)] ?? null
    }

    const getImportedSectionPreviewMeta = (sectionFrameId: number | string | null | undefined) => (
        getImportedSectionPreview(sectionFrameId)?.pageMeta ?? null
    )

    const getImportedSubpagesPreviewContent = (
        sectionFrameId: number | string | null | undefined,
        elementFrameId: number | string | null | undefined,
    ) => {
        const previewSection = getImportedSectionPreview(sectionFrameId)?.previewSection

        if (!previewSection) return null

        return getPreviewSubpagesContent(previewSection, elementFrameId)
    }

    const hydrateHomeImportedSections = async () => {
        if (!activePage.value?.isHome) {
            importedSectionPreviews.value = {}
            return
        }

        const sections = Array.isArray(activePage.value.content) ? activePage.value.content : []

        if (!dashboardStore.pages?.length) {
            await dashboardStore.fetchPages()
        }

        const homeVisiblePages = (dashboardStore.pages ?? []).filter((page: any) => !page.isHome && page.visible)

        if (!homeVisiblePages.length) {
            importedSectionPreviews.value = {}
            return
        }

        const previewCache = new Map<number, any>()
        const importedSectionsByFrameId = new Map<number | string, any>()

        await Promise.all(
            homeVisiblePages.map(async (page: any) => {
                try {
                    const response = await api.get(`site/${siteStore.id}/post/${page.id}`)
                    const payload = await response.json()

                    if (payload.status != '1') return

                    const previewPage = payload.content
                    const previewSections = Array.isArray(previewPage?.content) ? previewPage.content : []
                    const rootSection = previewSections[0]

                    previewCache.set(page.id, previewPage)

                    if (!rootSection?.frameId) return

                    importedSectionsByFrameId.set(rootSection.frameId, {
                        id: page.id,
                        name: page.name,
                        type: page.type,
                        route: page.route,
                    })
                } catch (error) {
                    console.warn(error)
                }
            }),
        )

        const nextImportedPreviews: Record<string, { pageMeta: any | null; previewSection: any | null }> = {}

        sections.forEach((section: any) => {
            const sectionId = section?.frameId

            if (sectionId == null) return

            const pageMeta = importedSectionsByFrameId.get(sectionId) ?? null
            const previewPage = pageMeta ? previewCache.get(pageMeta.id) : null
            const previewSections = Array.isArray(previewPage?.content) ? previewPage.content : []
            const previewSection =
                previewSections.find((item: any) => item.frameId === section.frameId) ?? previewSections[0]

            nextImportedPreviews[String(sectionId)] = {
                pageMeta,
                previewSection: isImportedHomePreviewCandidate(section) ? (previewSection ?? null) : null,
            }
        })

        importedSectionPreviews.value = nextImportedPreviews
    }

    const syncGalleryPresetExtras = () => {
        const galleryPresets = layoutStore.templateOrganismsCombinations?.maincontent ?? []

        if (!galleryPresets.length) return

        const syncElements = (elements: any[]) => {
            if (!Array.isArray(elements)) return

            elements.forEach((element: any) => {
                if (!element) return

                if (element.frameType === 'gallery') {
                    const presetId = Number(element?.extra?.maincontent_gallery_id ?? 0)

                    if (Number.isFinite(presetId) && presetId > 0) {
                        const matchedPreset = galleryPresets.find((preset: any) => Number(preset.id) === presetId)
                        const presetVariables = matchedPreset?.variables

                        if (presetVariables) {
                            element.extra = {
                                ...element.extra,
                                maincontent_gallery: presetVariables.maincontent_gallery,
                                maincontent_gallery_items: presetVariables.maincontent_gallery_items,
                                maincontent_gallery_items_mobile: presetVariables.maincontent_gallery_items_mobile,
                                maincontent_gallery_items_crop: presetVariables.maincontent_gallery_items_crop,
                                maincontent_gallery_padding: presetVariables.maincontent_gallery_padding,
                            }
                        }
                    }
                }

                if (Array.isArray(element.frameContent)) {
                    syncElements(element.frameContent)
                }
            })
        }

        syncElements(activePage.value?.content ?? [])
    }

    const reset = () => {
        previewDevice.value = 'desktop'
        previewSurface.value = 'editor'
        selectedSectionId.value = null
        selectedSectionIndex.value = null
        selectedElementId.value = null
        selectedElementType.value = null
        editingElementId.value = null
        sidebarReference.value = null
        elementForEdit.value = null
        activeTextEditor.value = null
        activeTextSelectionState.value = createDefaultActiveTextSelectionState()
        activeTextSelectionRange.value = null
        importedSectionPreviews.value = {}
        editorPageStore.setSectionForEdit(null)
        editorPageStore.setElementForEdit(null)
    }

    return {
        previewDevice,
        previewSurface,
        selectedSectionId,
        selectedSectionIndex,
        selectedElementId,
        selectedElementType,
        editingElementId,
        sidebarReference,
        sectionForEdit,
        elementForEdit,
        activeTextEditor,
        activeTextSelectionState,
        activeTextSelectionRange,
        importedSectionPreviews,
        isMobilePreview,
        isSitePreviewActive,
        setPreviewDevice,
        setPreviewSurface,
        togglePreviewSurface,
        transformPresetFrameToElement,
        getElementId,
        getSectionId,
        setSelectedSection,
        setSelectedSectionByIndex,
        clearSelectedSection,
        isSelectedSection,
        setSelectedElement,
        setSelectedElementBySchema,
        clearSelectedElement,
        setEditingElement,
        setEditingElementBySchema,
        clearEditingElement,
        isEditingElement,
        isElementActive,
        setActiveTextEditor,
        clearActiveTextEditor,
        setActiveTextSelectionState,
        setActiveTextSelectionRange,
        getTextEditorCommandChain,
        shouldKeepActiveTextSelection,
        setElementForEdit,
        updateElementBySchema,
        patchElementBySchema,
        updateElementForEdit,
        isFirstElementBySchema,
        isLastElementBySchema,
        moveElementBySchema,
        cloneElementBySchema,
        removeElementBySchema,
        addElementBySchema,
        appendTextElementToSection,
        addSectionAtIndex,
        deleteSectionAtIndex,
        moveSection,
        updateSectionExtra,
        setBackgroundImageSectionForEdit,
        setSidebarReference,
        clearSidebarTarget,
        getImportedSectionPreviewMeta,
        getImportedSubpagesPreviewContent,
        hydrateHomeImportedSections,
        syncGalleryPresetExtras,
        reset,
    }
})
